import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Divider, Chip, Stack, Skeleton, Alert,
  Tabs, Tab, Badge,
} from '@mui/material';
import {
  Close, LocalOffer, CheckCircle, NotificationsActive, Storefront,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useGetPosPromotionsQuery } from '../../services/promotionsApi';

// ─── Helpers ────────────────────────────────────────────────────────────────

function benefitSuffix(b, buyBucketName) {
  if (!b) return '';
  const dv  = b.discount_value;
  const fq  = b.free_quantity;
  const fp  = b.fixed_price;
  const sel = b.free_item_selection === 'cheapest' ? ' (cheapest)' : b.free_item_selection === 'costliest' ? ' (costliest)' : '';
  const getFrom = b.get_product_bucket_name && b.get_product_bucket_name !== buyBucketName
    ? ` from ${b.get_product_bucket_name}` : '';
  switch (b.benefit_type) {
    case 'percentage_off':   return `, get ${dv}% off`;
    case 'flat_off':         return `, get ₹${dv} off`;
    case 'free_items':       return `, get ${fq} free${getFrom}${sel}`;
    case 'discounted_items': return `, get ${fq} at ${dv}% off${getFrom}${sel}`;
    case 'fixed_price':      return ` for ₹${fp}`;
    default:                 return '';
  }
}

function conditionToHumanReadable(promo) {
  if (Array.isArray(promo.slabs) && promo.slabs.length > 0) {
    return [...promo.slabs]
      .sort((a, b) => a.slab_rank - b.slab_rank)
      .map((s) => {
        const c = s.condition;
        const b = s.benefit;
        if (!c) return '';
        const bucket = c.product_bucket_name ? ` from ${c.product_bucket_name}` : '';
        const suffix = benefitSuffix(b, c.product_bucket_name);
        if (c.condition_type === 'min_quantity')   return `Tier ${s.slab_rank}: Buy ${c.threshold_quantity}${bucket}${suffix}`;
        if (c.condition_type === 'min_value')      return `Tier ${s.slab_rank}: Spend ₹${c.threshold_value}${bucket}${suffix}`;
        if (c.condition_type === 'min_bill_value') return `Tier ${s.slab_rank}: Bill ₹${c.threshold_value}${suffix}`;
        return '';
      })
      .filter(Boolean)
      .join(' · ');
  }

  const c = promo.condition;
  const b = promo.benefit;
  if (!c || !b) return '';
  const suffix = benefitSuffix(b, c.product_bucket_name);

  switch (c.condition_type) {
    case 'min_quantity': {
      const bucket = c.product_bucket_name ? ` from ${c.product_bucket_name}` : '';
      return `Buy ${c.threshold_quantity} items${bucket}${suffix}`.trim();
    }
    case 'min_value': {
      const bucket = c.product_bucket_name ? ` from ${c.product_bucket_name}` : ' on qualifying items';
      return `Spend ₹${c.threshold_value}${bucket}${suffix}`;
    }
    case 'min_bill_value':
      return `Spend ₹${c.threshold_value} on bill${suffix}`;
    default:
      return '';
  }
}

function dayOfWeekLabel(days) {
  if (!days || days.length === 0) return null;
  const allWeekdays = [1, 2, 3, 4, 5];
  const allWeekends = [0, 6];
  if (allWeekdays.every(d => days.includes(d)) && days.length === 5) return 'Weekdays only';
  if (allWeekends.every(d => days.includes(d)) && days.length === 2) return 'Weekends only';
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days.map(d => names[d]).join(', ') + ' only';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const date = new Date(timeStr.includes('T') ? timeStr : `1970-01-01T${timeStr}`);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// ─── Sub-components ─────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: '0.58rem', fontWeight: 900, color: '#94A3B8',
    letterSpacing: '0.08rem', mb: 0.75, mt: 0.5,
  }}>
    {children}
  </Typography>
);

const tagChipSx = {
  fontSize: '0.6rem', height: 18,
  '& .MuiChip-label': { px: 0.75 },
};

function AppliedPromoCard({ promo }) {
  return (
    <Box sx={{ border: '1px solid #86EFAC', borderRadius: 2, p: 1.5, mb: 1, bgcolor: '#F0FDF4' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle sx={{ color: '#16A34A', fontSize: 17, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#14532D', lineHeight: 1.3 }}>
          {promo.promotion_name}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.68rem', color: '#15803D', mt: 0.5 }}>
        You saved ₹{parseFloat(promo.total_savings).toFixed(2)}
        {promo.affected_item_ids?.length > 0 && ` · ${promo.affected_item_ids.length} item${promo.affected_item_ids.length !== 1 ? 's' : ''} affected`}
      </Typography>
      <Chip
        label={promo.applicable_level === 'line_item' ? 'Line item' : 'Bill'}
        size="small"
        sx={{ ...tagChipSx, mt: 0.5, bgcolor: '#DCFCE7', color: '#15803D', border: 'none' }}
      />
    </Box>
  );
}

function NearMissCard({ hint }) {
  return (
    <Box sx={{ border: '1px solid #FCD34D', borderRadius: 2, p: 1.5, mb: 1, bgcolor: '#FFFBEB' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <NotificationsActive sx={{ color: '#D97706', fontSize: 17, flexShrink: 0, mt: 0.1 }} />
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#92400E', lineHeight: 1.3 }}>
            {hint.hint_message}
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#B45309', mt: 0.3 }}>
            {hint.promotion_name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function AvailablePromoCard({ promo, isApplied }) {
  const dayLabel  = dayOfWeekLabel(promo.day_of_week);
  const condition = conditionToHumanReadable(promo);
  const isSlabPromo = Array.isArray(promo.slabs) && promo.slabs.length > 0;

  return (
    <Box sx={{
      border: `1px solid ${isApplied ? '#86EFAC' : '#E5E7EB'}`,
      borderRadius: 2, p: 1.5, mb: 1,
      bgcolor: isApplied ? '#F0FDF4' : '#FAFAFA',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <LocalOffer sx={{ color: isApplied ? '#16A34A' : '#7C3AED', fontSize: 14, flexShrink: 0 }} />
          <Typography sx={{
            fontSize: '0.76rem', fontWeight: 600,
            color: isApplied ? '#14532D' : '#1E293B',
            lineHeight: 1.3, minWidth: 0,
          }}>
            {promo.name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          {isSlabPromo && (
            <Chip label="Tiered" size="small" sx={{ ...tagChipSx, bgcolor: '#EDE9FE', color: '#7C3AED', border: 'none' }} />
          )}
          {isApplied && (
            <Chip label="ACTIVE" size="small" sx={{ ...tagChipSx, bgcolor: '#DCFCE7', color: '#15803D', border: 'none' }} />
          )}
        </Stack>
      </Box>

      {condition && (
        <Typography sx={{ fontSize: '0.65rem', color: '#64748B', mt: 0.5, lineHeight: 1.4 }}>
          {condition}
        </Typography>
      )}

      {(dayLabel || promo.activation_period === 'specific_time' || promo.customer_groups?.length > 0) && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.6, gap: 0.4 }}>
          {dayLabel && (
            <Chip label={dayLabel} size="small" variant="outlined" sx={tagChipSx} />
          )}
          {promo.activation_period === 'specific_time' && promo.start_time && (
            <Chip label={`${formatTime(promo.start_time)}–${formatTime(promo.end_time)}`} size="small" variant="outlined" sx={tagChipSx} />
          )}
          {promo.customer_groups?.map(g => (
            <Chip key={g.id} label={`${g.name} only`} size="small" variant="outlined" sx={tagChipSx} />
          ))}
        </Stack>
      )}

      {promo.start_date && (
        <Typography sx={{ fontSize: '0.6rem', color: '#CBD5E1', mt: 0.4 }}>
          Valid: {formatDate(promo.start_date)} – {formatDate(promo.end_date)}
        </Typography>
      )}

      {/* Consumer Protection disclosure: offer discounts on kept items are
          reversed if the qualifying items are returned (backend-provided text). */}
      {promo.clawback_on_return && promo.clawback_policy_text && (
        <Typography sx={{ fontSize: '0.62rem', color: '#B45309', mt: 0.5, lineHeight: 1.4, fontStyle: 'italic' }}>
          {promo.clawback_policy_text}
        </Typography>
      )}
    </Box>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────

const PromotionsModal = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const { applied_promotions, near_miss_hints } = useSelector((s) => s.order);

  const { data, isLoading, isError } = useGetPosPromotionsQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: 60,
  });

  const allStorePromos = data?.data?.promotions || [];
  const appliedIds     = new Set(applied_promotions.map(p => p.promotion_id));

  const hasApplied   = applied_promotions.length > 0;
  const hasHints     = near_miss_hints.length > 0;
  const appliedCount = applied_promotions.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 0,
          position: 'absolute',
          top: '50%',
          left: '50%',
          bottom: 'auto',
          right: 'auto',
          transform: 'translate(-50%, -50%)',
          maxHeight: '78vh',
          width: 'calc(100% - 32px)',
          maxWidth: { xs: 'calc(100% - 32px)', sm: 560 },
        },
      }}
      sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center', p: 2 } }}
    >
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5, px: 2 }}>
        <LocalOffer sx={{ color: '#7C3AED', fontSize: 20 }} />
        <Typography component="div" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', flexGrow: 1 }}>
          Promotions
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          px: 2, minHeight: 40,
          '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', py: 0 },
          '& .MuiTabs-indicator': { backgroundColor: '#7C3AED' },
          '& .Mui-selected': { color: '#7C3AED !important' },
        }}
      >
        <Tab
          label={
            <Badge badgeContent={appliedCount} color="success" max={9}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14 } }}>
              <Box sx={{ pr: appliedCount > 0 ? 1.5 : 0 }}>Applied</Box>
            </Badge>
          }
        />
        <Tab
          icon={<Storefront sx={{ fontSize: 13 }} />}
          iconPosition="start"
          label="Available in store"
        />
      </Tabs>

      <Divider />

      {/* Body */}
      <DialogContent sx={{ px: 2, py: 1.5, overflowY: 'auto' }}>

        {/* ── TAB 0: Applied ── */}
        {activeTab === 0 && (
          <>
            {/* Applied promotions */}
            <SectionLabel>APPLIED ON THIS BILL</SectionLabel>
            {hasApplied
              ? applied_promotions.map(p => <AppliedPromoCard key={p.promotion_id} promo={p} />)
              : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <CheckCircle sx={{ fontSize: 36, color: '#E2E8F0', mb: 1 }} />
                  <Typography sx={{ fontWeight: 600, color: '#94A3B8', fontSize: '0.82rem' }}>
                    No promotions applied yet
                  </Typography>
                  <Typography sx={{ color: '#CBD5E1', fontSize: '0.72rem', mt: 0.4 }}>
                    Add items to the cart to qualify for offers
                  </Typography>
                </Box>
              )
            }

            {/* Near-miss hints */}
            {hasHints && (
              <>
                <SectionLabel>UPGRADE HINTS</SectionLabel>
                {near_miss_hints.map((h, i) => <NearMissCard key={`${h.promotion_id}-${i}`} hint={h} />)}
              </>
            )}

            {!hasApplied && !hasHints && (
              <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', textAlign: 'center', mt: 1 }}>
                Switch to "Available in store" to see what offers are running today
              </Typography>
            )}
          </>
        )}

        {/* ── TAB 1: Available in store ── */}
        {activeTab === 1 && (
          <>
            {isLoading && (
              <>
                <Skeleton variant="rounded" height={80} sx={{ mb: 1, borderRadius: 2 }} />
                <Skeleton variant="rounded" height={80} sx={{ mb: 1, borderRadius: 2 }} />
                <Skeleton variant="rounded" height={80} sx={{ mb: 1, borderRadius: 2 }} />
              </>
            )}

            {isError && (
              <Alert severity="error" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                Could not load promotions. Please try again.
              </Alert>
            )}

            {!isLoading && !isError && allStorePromos.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalOffer sx={{ fontSize: 48, color: '#E2E8F0', mb: 1.5 }} />
                <Typography sx={{ fontWeight: 700, color: '#94A3B8', fontSize: '0.9rem' }}>
                  No promotions active today
                </Typography>
                <Typography sx={{ color: '#CBD5E1', fontSize: '0.75rem', mt: 0.5 }}>
                  Ask your manager to set up offers for this store
                </Typography>
              </Box>
            )}

            {!isLoading && allStorePromos.length > 0 && (
              <>
                <SectionLabel>ALL OFFERS FOR THIS STORE</SectionLabel>
                {allStorePromos.map(p => (
                  <AvailablePromoCard key={p.id} promo={p} isApplied={appliedIds.has(p.id)} />
                ))}
              </>
            )}
          </>
        )}

      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'center', py: 1.25 }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#CBD5E1', textAlign: 'center' }}>
          Promotions are applied automatically on every cart change
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default PromotionsModal;
